import {Component, computed, inject, signal} from '@angular/core';
import {SideNav, SideNavItem} from '../../../../components/side-nav/side-nav';
import {PeppolStoreService} from '../../services/peppol-store.service';
import {PeriodNavigatorComponent, PeriodRange} from '../../../../components/period-navigator/period-navigator';
import {FinancesPeriodStore} from '../../services/finances-period.store';
import {
  CreditNotePeppolSummary,
  CreditNoteService,
  downloadFile,
  extractFilenameFromContentDisposition,
  InvoicePeppolSummary,
  InvoiceService
} from '../../../../../api';
import {ToastService} from '../../../../services/toast.service';
import {Loader} from '../../../../components/loader/loader';
import {IncomingInvoicesGrid} from '../incoming-invoices-grid/incoming-invoices-grid.component';
import {OutgoingInvoicesGrid} from '../outgoing-invoices-grid/outgoing-invoices-grid.component';
import {IncomingCreditNotesGrid} from '../incoming-credit-notes-grid/incoming-credit-notes-grid.component';
import {OutgoingCreditNotesGrid} from '../outgoing-credit-notes-grid/outgoing-credit-notes-grid.component';

export type PeppolView = 'incoming' | 'outgoing' | 'incoming-credit-notes' | 'outgoing-credit-notes';

@Component({
  selector: 'elcia-peppol-home',
  imports: [SideNav, PeriodNavigatorComponent, Loader, IncomingInvoicesGrid, OutgoingInvoicesGrid, IncomingCreditNotesGrid, OutgoingCreditNotesGrid],
  providers: [PeppolStoreService],
  templateUrl: './peppol-home.html',
  styleUrl: './peppol-home.scss',
})
export class PeppolHome {
  activeView = signal<PeppolView>('incoming');

  readonly navItems: SideNavItem<PeppolView>[] = [
    {value: 'incoming', label: 'entity.incoming-invoices'},
    {value: 'outgoing', label: 'entity.outgoing-invoices'},
    {value: 'incoming-credit-notes', label: 'entity.incoming-credit-notes'},
    {value: 'outgoing-credit-notes', label: 'entity.outgoing-credit-notes'},
  ];
  protected periodStore = inject(FinancesPeriodStore);
  protected store = inject(PeppolStoreService);
  protected invoiceService = inject(InvoiceService);
  protected creditNoteService = inject(CreditNoteService);


  outgoingInvoices = computed(() => this.store.outgoingInvoices());
  isOutgoingLoading = computed(() => this.store.isOutgoingLoading())
  isIncomingLoading = computed(() => this.store.isIncomingLoading())
  incomingInvoices = computed(() => this.store.incomingInvoices());

  incomingCreditNotes = computed(() => this.store.incomingCreditNotes());
  isIncomingCreditNotesLoading = computed(() => this.store.isIncomingCreditNotesLoading());
  outgoingCreditNotes = computed(() => this.store.outgoingCreditNotes());
  isOutgoingCreditNotesLoading = computed(() => this.store.isOutgoingCreditNotesLoading());


  protected setPeriod($event: PeriodRange) {
    this.periodStore.period.set($event);
  }

  private toastService = inject(ToastService);

  // TODO?: Insert this code in store to invalid query while mutation ?. IDK hard isolation of use case
  protected downloadInvoicePeppol(invoice: InvoicePeppolSummary): void {
    if (!invoice.uuid) return;
    this.invoiceService.getInvoicesPeppolFile([invoice.uuid], 'response')
      .subscribe({
        next: response => {
          const filename = extractFilenameFromContentDisposition(
            response.headers.get('Content-Disposition'),
            `${invoice.number ?? invoice.uuid}`
          );
          downloadFile(response.body!, filename);
          if (this.activeView() === 'incoming') {
            this.store.invalidIncoming();
          } else {
            this.store.invalidOutgoing();
          }
        }, error: (error) => {
          if (error.status === 404) {
            this.toastService.showMessage({severity: 'info', summary: 'app.not-found', detail: 'app.no-attachments'})
          }
        }
      });
  }

  protected downloadAttachments(invoice: InvoicePeppolSummary): void {
    if (!invoice.uuid) return;
    this.invoiceService.getInvoicesAttachments([invoice.uuid], 'response')
      .subscribe({
        next: response => {
          const filename = extractFilenameFromContentDisposition(
            response.headers.get('Content-Disposition'),
            `${invoice.number ?? invoice.uuid}`
          );
          downloadFile(response.body!, filename);
        }, error: (error) => {
          if (error.status === 404) {
            this.toastService.showMessage({severity: 'info', summary: 'app.not-found', detail: 'app.no-attachments'})
          }
        }
      });
  }

  protected downloadCreditNotePeppol(creditNote: CreditNotePeppolSummary): void {
    if (!creditNote.uuid) return;
    this.creditNoteService.getCreditNotesPeppolFile([creditNote.uuid], 'response')
      .subscribe({
        next: response => {
          const filename = extractFilenameFromContentDisposition(
            response.headers.get('Content-Disposition'),
            `${creditNote.number ?? creditNote.uuid}`
          );
          downloadFile(response.body!, filename);
          if (this.activeView() === 'incoming-credit-notes') {
            this.store.invalidIncomingCreditNotes();
          } else {
            this.store.invalidOutgoingCreditNotes();
          }
        }, error: (error) => {
          if (error.status === 404) {
            this.toastService.showMessage({severity: 'info', summary: 'app.not-found', detail: 'app.no-attachments'})
          }
        }
      });
  }

  protected downloadCreditNoteAttachments(creditNote: CreditNotePeppolSummary): void {
    if (!creditNote.uuid) return;
    this.creditNoteService.getCreditNotesAttachments([creditNote.uuid], 'response')
      .subscribe({
        next: response => {
          const filename = extractFilenameFromContentDisposition(
            response.headers.get('Content-Disposition'),
            `${creditNote.number ?? creditNote.uuid}`
          );
          downloadFile(response.body!, filename);
        }, error: (error) => {
          if (error.status === 404) {
            this.toastService.showMessage({severity: 'info', summary: 'app.not-found', detail: 'app.no-attachments'})
          }
        }
      });
  }
}
